import axios from "axios";
import { decrementLoading, incrementLoading } from "./loadingService";

//  Using CRA environment variable with a same-origin fallback for graceful dev startup
const rawApiUrl =
 process.env.REACT_APP_API_URL ||
"http://localhost:5000/api/v1";
const API_BASE_URL =
  rawApiUrl
    .split(",")
    .map((value) => value.trim())
    .find(Boolean) || "/api/v1";

// Axios instance
// NOTE: We intentionally do NOT set a global "Content-Type" here. Axios will
// automatically set `application/json` for plain objects, and for FormData
// payloads it lets the browser generate the correct `multipart/form-data`
// boundary automatically (an explicit application/json header would break
// multipart uploads).
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  withCredentials: true, // IMPORTANT for CORS + cookies
});

// ================= REQUEST INTERCEPTOR =================
apiClient.interceptors.request.use(
  (config) => {
    incrementLoading();

    // Only default to JSON when the payload is NOT FormData. This keeps normal
    // JSON API requests intact while allowing media uploads (which send
    // FormData) to use the browser-generated multipart boundary.
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    decrementLoading();
    return Promise.reject(error);
  }
);

// ================= RESPONSE INTERCEPTOR =================
let isRefreshing = false;
let refreshSubscribers = [];

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

apiClient.interceptors.response.use(
  (response) => {
    decrementLoading();
    return response;
  },
  async (error) => {
    decrementLoading();
    const originalRequest = error.config || {};

    // Robust auth-route detection to prevent refresh/ retry loops
    const fullUrl = `${originalRequest.baseURL || ""}${originalRequest.url || ""}`;
    const isAuthRoute = fullUrl.includes("/auth/") || fullUrl.includes("/auth");

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthRoute
    ) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) throw new Error("No refresh token");

        const res = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refreshToken },
          { withCredentials: true }
        );

        const { accessToken, refreshToken: newRefreshToken } = res.data;

        //  Save tokens
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        //  Update default header properly
        apiClient.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

        onTokenRefreshed(accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (err) {
        //  logout fallback
        localStorage.clear();
        window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ================= APIs =================

// Auth
export const authAPI = {
  register: (data) => apiClient.post("/auth/register", data),
  login: (data) => apiClient.post("/auth/login", data),
  getProfile: () => apiClient.get("/auth/profile"),
  logout: (data) => apiClient.post("/auth/logout", data || {}),
  logoutAll: () => apiClient.post("/auth/logout-all"),

  // Forgot/Reset Password (placeholders - backend must implement these endpoints)
  forgotPassword: (data) => apiClient.post("/auth/forgot-password", data),
  resetPassword: (data) => apiClient.post("/auth/reset-password", data),
};


// Products
export const productAPI = {
  getProducts: (params) => apiClient.get("/products", { params }),
  getProductById: (id) => apiClient.get(`/products/${id}`),
};

// Storefront settings
export const settingsAPI = {
  getPaymentOptions: () => apiClient.get("/settings/payment-options"),
};

// Cart
export const cartAPI = {
  getCart: () => apiClient.get("/cart"),
  addToCart: (productId, quantity = 1) =>
  apiClient.post("/cart/add", {
    productId,
    quantity,
  }),
  updateQuantity: (productId, quantity) =>
    apiClient.put("/cart/update", { productId, quantity }),
  removeFromCart: (productId) =>
    apiClient.post("/cart/remove", { productId }),
};

// Wishlist
export const wishlistAPI = {
  getWishlist: () => apiClient.get("/wishlist"),
  addToWishlist: (productId) =>
    apiClient.post("/wishlist/add", { productId }),
  removeFromWishlist: (productId) =>
    apiClient.post("/wishlist/remove", { productId }),
};

// Orders
export const orderAPI = {
  placeOrder: (data) => apiClient.post("/orders", data),
  getOrders: () => apiClient.get("/orders"),
  getOrderById: (id) => apiClient.get(`/orders/${id}`),
  cancelOrder: (id) => apiClient.put(`/orders/${id}/cancel`),
  returnOrder: (id) => apiClient.put(`/orders/${id}/return`),
};

// Payments
export const paymentAPI = {
  createOrder: (data) => apiClient.post("/payment/create-order", data),
  verifyPayment: (data) => apiClient.post("/payment/verify", data),
};

// Contact
export const contactAPI = {
  sendMessage: (data) => apiClient.post("/contact", data),
};

// Reviews
export const reviewAPI = {
  getProductReviews: (productId, params) =>
    apiClient.get(`/reviews/product/${productId}`, { params }),
  getProductReviewStats: (productId) =>
    apiClient.get(`/reviews/product/${productId}/stats`),
  submitReview: (productId, data) =>
    apiClient.post(`/reviews/product/${productId}`, data),
};

export default apiClient;
